import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import internTestApi from "../../api/serviceApi/internTestApi";

export default function TestPage() {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flagged, setFlagged] = useState({});

  const attemptId = sessionStorage.getItem("attemptId");
  const submittedRef = useRef(false);

  /* 🔒 Block back & refresh */
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = () =>
      window.history.pushState(null, "", window.location.href);

    window.onbeforeunload = () => "Test in progress";
    return () => (window.onbeforeunload = null);
  }, []);

  /* Fetch test */
  useEffect(() => {
    if (!attemptId) {
      navigate(`/intern-test/${testId}/start`, { replace: true });
      return;
    }

    internTestApi.get(`api/intern-test/${testId}`).then(res => {
      setTest(res.data);
      setTimeLeft(res.data.duration * 60);
    });
  }, [testId, attemptId, navigate]);

  /* Timer */
  useEffect(() => {
    if (!test) return;

    if (timeLeft <= 0 && !submittedRef.current) {
      handleSubmit(true);
      return;
    }

    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, test]);

  /* Keyboard navigation A/B/C/D */
  useEffect(() => {
    const handler = e => {
      const key = e.key.toUpperCase();
      if (["A", "B", "C", "D"].includes(key)) {
        handleSelect(test.questions[currentIndex]._id, key);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [currentIndex, test]);

  const handleSelect = (qid, opt) => {
    setAnswers(prev => ({ ...prev, [qid]: opt }));
  };

  const toggleFlag = qid => {
    setFlagged(prev => ({ ...prev, [qid]: !prev[qid] }));
  };

  const handleSubmit = async (autoSubmitted = false) => {
    if (submittedRef.current) return;

    if (
      !autoSubmitted &&
      Object.keys(answers).length !== test.questions.length
    ) {
      alert("All questions are mandatory.");
      return;
    }

    submittedRef.current = true;

    await internTestApi.post(`api/intern-test/${testId}/submit`, {
      attemptId,
      answers: Object.entries(answers).map(([qid, opt]) => ({
        questionId: qid,
        selectedOption: opt
      })),
      autoSubmitted
    });

    sessionStorage.clear();
    navigate(`/intern-test/${testId}/thank-you`, { replace: true });
  };

  if (!test) return <p className="p-10">Loading…</p>;

  const question = test.questions[currentIndex];
  const total = test.questions.length;
  const progress = Math.round(
    (Object.keys(answers).length / total) * 100
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">

      {/* HEADER */}
      <div className="bg-white shadow px-6 py-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-semibold">Intern Assessment</h2>
          <span className="text-red-600 font-mono">
            ⏱ {Math.floor(timeLeft / 60)}:
            {String(timeLeft % 60).padStart(2, "0")}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded h-2">
          <div
            className="bg-blue-600 h-2 rounded"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* MAIN */}
      <div className="flex flex-1 overflow-hidden">

        {/* QUESTION PALETTE */}
        <aside className="w-64 bg-white border-r p-4 overflow-y-auto">
          <h3 className="font-semibold mb-3">Questions</h3>
          <div className="grid grid-cols-5 gap-2">
            {test.questions.map((q, i) => (
              <button
                key={q._id}
                onClick={() => setCurrentIndex(i)}
                className={`text-sm rounded py-2
                  ${
                    answers[q._id]
                      ? "bg-green-500 text-white"
                      : "bg-gray-200"
                  }
                  ${
                    flagged[q._id]
                      ? "ring-2 ring-yellow-400"
                      : ""
                  }
                  ${
                    i === currentIndex
                      ? "ring-2 ring-blue-600"
                      : ""
                  }
                `}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <div className="mt-4 text-xs text-gray-600">
            <p>🟩 Answered</p>
            <p>🟨 Flagged</p>
          </div>
        </aside>

        {/* QUESTION CARD */}
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="bg-white w-full max-w-3xl p-8 rounded shadow">

            <div className="flex justify-between mb-4">
              <p className="font-semibold">
                Q{currentIndex + 1}. {question.questionText}
              </p>
              <button
                onClick={() => toggleFlag(question._id)}
                className="text-sm text-yellow-600"
              >
                {flagged[question._id]
                  ? "★ Flagged"
                  : "☆ Flag for review"}
              </button>
            </div>

            <div className="space-y-3">
              {["A", "B", "C", "D"].map(o => (
                <label
                  key={o}
                  className={`block border rounded p-3 cursor-pointer
                    ${
                      answers[question._id] === o
                        ? "border-blue-600 bg-blue-50"
                        : "hover:border-gray-400"
                    }
                  `}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={answers[question._id] === o}
                    onChange={() =>
                      handleSelect(question._id, o)
                    }
                  />
                  <b>{o}.</b> {question[`option${o}`]}
                </label>
              ))}
            </div>
          </div>
        </main>
      </div>

      {/* FOOTER */}
      <div className="bg-white border-t px-6 py-4 flex justify-between">
        <button
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex(i => i - 1)}
          className="px-4 py-2 border rounded disabled:opacity-40"
        >
          Previous
        </button>

        {currentIndex === total - 1 ? (
          <button
            onClick={() => handleSubmit(false)}
            className="bg-black text-white px-6 py-2 rounded"
          >
            Submit Test
          </button>
        ) : (
          <button
            onClick={() => setCurrentIndex(i => i + 1)}
            className="bg-blue-600 text-white px-6 py-2 rounded"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
